import * as ids from 'short-id';
import { omit } from 'underscore';
import { CropMetadata, Dimensions, Size } from 'types';
import { Pixels, ViewportWidth } from 'utils/measurement';
import {
  fitElement,
  hasPosition,
  isFill,
  measurementToPx,
  measurementToViewport,
  replaceRect,
  scale,
} from 'utils/placement';
import {
  createNewLayer,
  deleteLayer,
  getRemainingAssetCountInLayer,
} from '../../shared/layers';
import { updateState } from '../../shared/mutations';
import { Slide, VideoTemplateStateContent } from '../../types';
import { deleteVideo } from '../video/mutations';
import { DEFAULT_IMAGE_EFFECT } from './constants';
import { getFirstSlide } from './selectors';
import { AddImageOptions, AddMediaMetaData } from './types';
import { isLottie } from './utils';

export function deleteImage(
  state: VideoTemplateStateContent,
  id: string,
  keepLayer = false,
) {
  const { mainImage, slideshow } = state;
  const slide = slideshow.data[id];
  const isLottieSlide = isLottie(slide);

  const { layerId } = slide;

  const nAssetsLeftInLayer = getRemainingAssetCountInLayer(state, layerId);

  const data = isLottieSlide
    ? {
        ...slideshow.data,
        [id]: {
          ...slideshow.data[id],
          imageSrc: '',
          metadata: undefined,
          originalSrc: '',
        },
      }
    : omit(slideshow.data, id);

  const order = isLottieSlide
    ? slideshow.order
    : slideshow.order.filter(otherId => otherId !== id);

  return {
    ...state,
    // if keep layer is enabled, layer deletion is omitted
    ...(nAssetsLeftInLayer === 0 && !keepLayer
      ? deleteLayer(state, layerId)
      : undefined),
    // if the slide being deleted was the main image, clear out the main image
    // id but leave the type unchanged. when the user adds another image, the
    // mainImage type will indicate that the new image should become the new
    // main image
    mainImage: mainImage && {
      ...mainImage,
      ids: mainImage.ids.filter(currId => isLottieSlide || currId !== id),
    },
    slideshow: {
      newImages: slideshow.newImages.filter(otherId => otherId !== id),
      data,
      order,
    },
  };
}

export function replaceImage(
  state: VideoTemplateStateContent,
  id: string,
  src: Blob | string,
  previewType: 'image' | 'none' | 'videoClip',
  {
    original,
    metadata,
    fileName,
    integrationData,
  }: Omit<AddImageOptions, 'id'>,
): VideoTemplateStateContent {
  if (previewType === 'none') {
    return state;
  }

  const { slideshow, videoClips } = state;
  const { newImages } = slideshow;

  // This new condition allows the possibility of replacing a video/gif with an image
  // asset. When this case is matched, target layer id, then video asset is removed
  // but its layer kept and new image is added using the target layer in order to avoid
  // adding it in a new layer.
  if (previewType === 'videoClip') {
    const videoClip = videoClips.data[id];
    const targetLayerId = videoClip?.layerId;
    return addImage(
      deleteVideo(state, id, true),
      src,
      { fileName, id, original, metadata, integrationData },
      { placement: videoClip.placement, targetLayerId },
    );
  }

  return updateState(state, {
    slideshow: {
      newImages: newImages.indexOf(id) >= 0 ? newImages : [...newImages, id],
      data: {
        [id]: {
          fileName,
          metadata,
          integrationData,
          imageSrc: src,
          originalSrc: original,
        },
      },
    },
  });
}

export function addImage(
  state: VideoTemplateStateContent,
  src: string | Blob | File,
  {
    fileName,
    id = ids.generate(),
    original,
    metadata,
    integrationData,
  }: AddImageOptions = {},
  { placement, targetLayerId }: AddMediaMetaData = {},
): VideoTemplateStateContent {
  const {
    mainImage,
    slideshow: { order, newImages, data },
  } = state;

  const isMainImage = mainImage?.ids.includes(id);

  const name =
    typeof src === 'string' ? fileName : (src as any)?.name ?? fileName;

  // on multi-layer lottie templates, it is required to check if targetLayerId exists.
  // this ensures that when replacing videoclip with an image it wont replace the main
  // lottie image.
  const mainLottieImageIds = mainImage?.ids.filter(currId =>
    isLottie(data[currId]),
  );
  if (mainLottieImageIds?.length && !targetLayerId) {
    return mainLottieImageIds.reduce((draftState, lottieImageId) => {
      return replaceImage(draftState, lottieImageId, src, 'image', {
        fileName,
        integrationData,
        metadata,
        original,
      });
    }, state);
  }

  const [layerId, layers] = createNewLayer(state, 'media', targetLayerId);

  const newSlide: Slide = {
    metadata,
    id,
    fileName: name,
    layerId,
    originalSrc: original,
    imageEffect: DEFAULT_IMAGE_EFFECT,
    imageSrc: src,
    imageType: isMainImage ? ('mainImage' as const) : undefined,
    integrationData,
    placement,
  };

  const slideshow = {
    data: {
      ...data,
      [newSlide.id]: newSlide,
    },
    newImages: [...newImages, newSlide.id],
    order: [...order, newSlide.id],
  };

  return updateState(state, {
    mainImage: !isMainImage
      ? mainImage
      : {
          ...mainImage,
          ids: [...mainImage.ids, id],
        },
    layers,
    slideshow,
  });
}

export function recropImage(
  state: VideoTemplateStateContent,
  id: string,
  src: Blob,
  metadata: CropMetadata,
): VideoTemplateStateContent {
  const { slideshow } = state;
  const { newImages } = slideshow;

  return {
    ...state,
    slideshow: {
      ...slideshow,
      newImages: newImages.indexOf(id) >= 0 ? newImages : [...newImages, id],
      data: {
        ...slideshow.data,
        [id]: {
          ...slideshow.data[id],
          metadata,
          imageSrc: src,
        },
      },
    },
  };
}

export function scaleImage(
  state: VideoTemplateStateContent,
  id: string,
  multiplier: number,
): VideoTemplateStateContent {
  const { canvas, slideshow } = state;
  const slide = slideshow.data[id];

  return updateState(state, {
    slideshow: {
      data: {
        [id]: {
          placement: measurementToViewport(
            scale(measurementToPx(slide.placement, canvas), multiplier, {
              minArea: 500,
            }),
            canvas,
          ),
        },
      },
    },
  });
}

export function recropMainImage(
  state: VideoTemplateStateContent,
  src: Blob,
  metadata: CropMetadata,
): VideoTemplateStateContent {
  const { mainImage } = state;

  return mainImage.ids.reduce(
    (draftState, currId) => recropImage(draftState, currId, src, metadata),
    state,
  );
}

export function deleteOnlyImage(state: VideoTemplateStateContent) {
  const slide = getFirstSlide(state);
  return !slide ? state : deleteImage(state, slide.id);
}

export function replaceMainImage(
  state: VideoTemplateStateContent,
  url: string | Blob,
  metadata?: Omit<AddImageOptions, 'id'>,
): VideoTemplateStateContent {
  const { mainImage } = state;

  // main image a slideshow image.  if there's an id, just replace the image source
  if (mainImage?.ids.length) {
    return mainImage.ids.reduce(
      (draftState, currId) =>
        replaceImage(draftState, currId, url, 'image', {
          ...metadata,
          original: metadata?.original ?? url,
        }),
      state,
    );
  }

  // main image is slideshow but no image id.  add a new slide
  return addImage(state, url, {
    ...metadata,
    original: url,
  });
}

export function updateSlidePlacement(
  state: VideoTemplateStateContent,
  id: string,
  placement: Dimensions<Pixels>,
): VideoTemplateStateContent {
  const { canvas } = state;

  return updateState(state, {
    slideshow: {
      data: {
        [id]: {
          placement: measurementToViewport(
            {
              height: placement.height,
              left: placement.left,
              top: placement.top,
              width: placement.width,
            },
            canvas,
          ),
        },
      },
    },
  });
}

export function updateSlideBlurRadius(
  state: VideoTemplateStateContent,
  id: string,
  blurRadius: ViewportWidth,
) {
  return updateState(state, {
    slideshow: {
      data: {
        [id]: {
          blurRadius,
        },
      },
    },
  });
}

export function updateSlideBorderRadius(
  state: VideoTemplateStateContent,
  id: string,
  borderRadius: ViewportWidth,
) {
  return updateState(state, {
    slideshow: {
      data: {
        [id]: {
          borderRadius,
        },
      },
    },
  });
}

export function setInitialSlidePlacement(
  state: VideoTemplateStateContent,
  id: string,
  naturalSize: Size<Pixels>,
): VideoTemplateStateContent {
  const { canvas, slideshow } = state;
  const canvasPx = {
    height: new Pixels(canvas.height),
    width: new Pixels(canvas.width),
  };

  const slide = slideshow.data[id];

  const placement = (() => {
    if (!hasPosition(slide.placement)) {
      return fitElement(naturalSize, canvasPx, 'fit');
    }

    const slidePlacement = measurementToPx(slide.placement, canvasPx);

    if (isFill(slidePlacement, canvasPx)) {
      return fitElement(naturalSize, canvasPx, 'fill');
    }

    return replaceRect(slidePlacement, naturalSize, canvasPx);
  })();

  return updateState(state, {
    slideshow: {
      data: {
        [id]: {
          placement: measurementToViewport(
            {
              height: placement.height,
              left: placement.left,
              top: placement.top,
              width: placement.width,
            },
            canvas,
          ),
        },
      },
    },
  });
}
